import axios from "axios";
import { useEffect, useState } from "react";
import { BACKEND_AI_AGENT_URL } from "../../../../config";
import { prettifyNumber } from "../../../../utils/utils";
import {
  Flame,
  ShoppingBadge,
  Telegram,
  ThumbUp,
  Twitter,
  Webpage,
} from "../assets";
import { CEXLogos } from "./CexLogos/CexLogos";
import { SentimentBar } from "./SentimentBar";

interface TokenInformationResponse {
  name: string;
  symbol: string;
  price: number;
  percentagePrice24hChange: number;
  volume24h: number;
  percentageVolume24hChange: number;
  marketCap: number;
  liquidity: number;
  cexAvailability: string[];
  percentageCirculatingSupply: number;
  aiRecommendation: number;
  rank: number;
}

interface Props {
  projectId: string;
}

export const TokenInformation = ({ projectId }: Props) => {
  const [tokenInfo, setTokenInfo] = useState<TokenInformationResponse>({
    name: "",
    symbol: "",
    price: 0,
    percentagePrice24hChange: 0,
    volume24h: 0,
    percentageVolume24hChange: 0,
    marketCap: 0,
    liquidity: 0,
    cexAvailability: [],
    percentageCirculatingSupply: 0,
    aiRecommendation: 0,
    rank: 0,
  });

  const [projectInformation, setProjectInformation] = useState({
    projectId: "",
    projectDescription: "Loading...",
    tags: ["..."],
    trendingTags: [""],
    backers: ["Loading"],
    securityAlerts: "",
    risk: "",
    smartContractsCorrectness: "",
    links: {
      twitter: undefined,
      telegram: undefined,
      webpage: undefined,
    },
  });

  useEffect(() => {
    const fetchProjectInformation = async () => {
      try {
        const response = await fetch(
          `${BACKEND_AI_AGENT_URL}/project-information?projectId=${projectId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
        const data = await response.json();

        setProjectInformation(data);
      } catch (error) {
        console.error("Failed to fetch project information data:", error);
      } finally {
      }
    };

    fetchProjectInformation();
  }, [projectId]);

  useEffect(() => {
    const fetchTrendingData = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_AI_AGENT_URL}/token-information`,
          { params: { projectId } },
        );
        const data: TokenInformationResponse = await response.data;
        if (data) {
          setTokenInfo(data);
        }
      } catch (error) {
        console.error("Failed to fetch token information", error);
      } finally {
      }
    };

    fetchTrendingData();
  }, [projectId]);

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="flex gap-4 items-center">
        <span className="font-ibm-plex-sans text-[24px] font-bold leading-[32px]">
          {`#${tokenInfo.rank}`}
        </span>
        <img
          className="w-[38px] h-[38px]"
          src={`https://mygateway.io/assets/img/tokens/${tokenInfo.symbol}.webp`}
          alt="token"
        />
        <div className="flex gap-1 font-ibm-plex-sans text-[14px] font-medium leading-[20px]">
          <span>{tokenInfo.name}</span>
          <span className="text-[#FFFFFF88]">{tokenInfo.symbol}</span>
        </div>
        <div className="grow"></div>
        <div className="flex flex-nowrap gap-2 items-center">
          <a href={projectInformation?.links.twitter}>
            <Twitter />
          </a>
          <a href={projectInformation?.links.telegram}>
            <Telegram />
          </a>
          <a href={projectInformation?.links.webpage}>
            <Webpage />
          </a>
        </div>
      </div>
      <div className="flex gap-2">
        {projectInformation?.tags.slice(0, 2).map((tag, index) => (
          <div
            className="relative flex flex-nowrap text-nowrap
 gap-1 items-center bg-[#55A6FB1A] rounded-[6px] px-2 py-1 text-[10px] "
            key={`${tag}-${index}`}
          >
            {tag}
            <ShoppingBadge height="14px" />
            {projectInformation?.trendingTags.includes(tag) && (
              <Flame
                height="14px"
                className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2"
              />
            )}
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">
            Price
          </span>
          <div className="flex items-end gap-1">
            <span className="font-ibm-plex-sans text-[16px] font-medium leading-[24px]">
              {`${prettifyNumber(tokenInfo.price, 4)} USD`}
            </span>
            <span
              className={`font-ibm-plex-sans text-[12px] font-medium leading-[20px] ${tokenInfo.percentagePrice24hChange >= 0 ? "text-[#4CAF50]" : "text-[#F44336]"}`}
            >
              {`${tokenInfo.percentagePrice24hChange > 0 ? "+" : ""}${prettifyNumber(tokenInfo.percentagePrice24hChange)}%`}
            </span>
          </div>
        </div>
        <div className="flex grow h-[1px] bg-[#FFFFFF0D]"></div>
        <div className="flex justify-between items-center">
          <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">
            Volume (24h)
          </span>
          <div className="flex items-end gap-1">
            <span className="font-ibm-plex-sans text-[16px] font-medium leading-[24px]">
              {`${tokenInfo.volume24h.toLocaleString(undefined, { maximumFractionDigits: 0 })} USD`}
            </span>
            <span
              className={`font-ibm-plex-sans text-[12px] font-medium leading-[20px] ${tokenInfo.percentageVolume24hChange >= 0 ? "text-[#4CAF50]" : "text-[#F44336]"}`}
            >
              {`${prettifyNumber(tokenInfo.percentageVolume24hChange)}%`}
            </span>
          </div>
        </div>
        <div className="flex grow h-[1px] bg-[#FFFFFF0D]"></div>
        <div className="flex justify-between items-center">
          <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">
            Market Cap
          </span>
          <span className="font-ibm-plex-sans text-[16px] font-medium leading-[24px]">
            {`${tokenInfo.marketCap.toLocaleString(undefined, { maximumFractionDigits: 0 })} USD`}
          </span>
        </div>
        <div className="flex grow h-[1px] bg-[#FFFFFF0D]"></div>
        <div className="flex justify-between items-center">
          <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">
            Liquidity
          </span>
          <div className="flex items-center gap-1">
            <span className="font-ibm-plex-sans text-[16px] font-medium leading-[24px]">
              {`${tokenInfo.liquidity.toLocaleString(undefined, { maximumFractionDigits: 0 })} USD`}
            </span>
            <ThumbUp />
          </div>
        </div>
        <div className="flex grow h-[1px] bg-[#FFFFFF0D]"></div>
        <div className="flex justify-between items-center">
          <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">
            CEX Availability
          </span>
          <div className="flex items-end gap-1">
            <CEXLogos cexes={tokenInfo.cexAvailability} />
          </div>
        </div>
        <div className="flex grow h-[1px] bg-[#FFFFFF0D]"></div>
        <div className="flex justify-between items-center">
          <span className="font-ibm-plex-sans text-[14px] font-normal leading-[20px] text-[#FFFFFF88]">
            Supply in circulation
          </span>
          <span className="font-ibm-plex-sans text-[16px] font-medium leading-[24px]">
            {`${prettifyNumber(tokenInfo.percentageCirculatingSupply)}%`}
          </span>
        </div>
      </div>
      <div />
      <div className="flex flex-col gap-4 bg-[#55A6FB1A] -mx-[20px] -mb-[20px] -mt-[16px] px-[30px] pb-[20px] pt-[16px] rounded-b-[8px]">
        <span className="font-ibm-plex-sans text-[16px] font-bold leading-[24px] text-left">
          AI Recommendation
        </span>
        <SentimentBar percentage={tokenInfo.aiRecommendation} />
      </div>
    </div>
  );
};
